import { fetch as fetchRaw } from '@lib/fetch';
import { getDefinedUrlSearchParams } from '@lib/util';
import type {
  ExportNetworkDataPointsCatalogueResponse,
  ExportNetworkRequest,
  VectorSearchQuery,
  VectorSearchResponse,
} from '@retracedgmbh/modules';
import { backend } from '@services/services';
import type {
  ApiSuccessEnvelope,
  CompanyJoinRequest,
  CompanyJoinRequestRole,
  DirectorySearchQuery,
  DirectorySearchResponse,
  FetchResult,
  PendingCompanyJoinRequest,
} from './types';

const fetch = (url: string, ...args: unknown[]) =>
  fetchRaw([backend.url, url], ...args);

/**
 * Triggers an async companies network export.
 * The backend responds 204 immediately and delivers the file via email
 * once the background job completes.
 */
export function networkCompaniesExport(
  body: ExportNetworkRequest,
): Promise<FetchResult<null>> {
  return fetch('/companies/export', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * The data point catalogue behind the export dialog's picker.
 *
 * POST rather than GET because it carries up to 500 company ids: the answered
 * counts describe exactly the companies being exported, so the picker's numbers
 * and the file's columns come from the same scope.
 */
export function fetchNetworkExportCatalogue(
  ids: string[],
  includeOwnCompany: boolean,
): Promise<FetchResult<ExportNetworkDataPointsCatalogueResponse>> {
  return fetch('/companies/export/options', {
    method: 'POST',
    body: JSON.stringify({ ids, includeOwnCompany }),
  }) as Promise<FetchResult<ExportNetworkDataPointsCatalogueResponse>>;
}

export function getCompaniesListVectorSearch(
  queryParams: VectorSearchQuery,
): Promise<FetchResult<VectorSearchResponse>> {
  return fetch(
    `/companies/vector-search?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<FetchResult<VectorSearchResponse>>;
}

// Read-only company search reachable without a company context — powers the
// companyless landing page (`/onboarding/company-search`). Unlike
// `getCompaniesListVectorSearch`, has no `excludeCompanyIds` param and no
// connect/create action.
export function getCompaniesDirectorySearch(
  queryParams: DirectorySearchQuery,
): Promise<FetchResult<DirectorySearchResponse>> {
  return fetch(
    `/companies/directory-search?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<FetchResult<DirectorySearchResponse>>;
}

// Companyless user asks to join a company found via the directory search.
// Backend replies via `reply.apiSuccess(result)` — envelope, not the payload.
export function postCompanyJoinRequest(
  companyId: string,
): Promise<FetchResult<ApiSuccessEnvelope<CompanyJoinRequest>>> {
  return fetch('/companies/join-requests', {
    method: 'POST',
    body: JSON.stringify({ companyId }),
  }) as Promise<FetchResult<ApiSuccessEnvelope<CompanyJoinRequest>>>;
}

// Company owner reviewing pending join requests for their own company.
// Backend replies via `reply.apiSuccess(result)` — envelope, not the payload.
export function getPendingCompanyJoinRequests(): Promise<
  FetchResult<ApiSuccessEnvelope<PendingCompanyJoinRequest[]>>
> {
  return fetch('/companies/join-requests/pending', {
    method: 'GET',
  }) as Promise<FetchResult<ApiSuccessEnvelope<PendingCompanyJoinRequest[]>>>;
}

// Backend replies `204 No Content` on success — no body.
export function postApproveCompanyJoinRequest(
  id: string,
  role: CompanyJoinRequestRole,
): Promise<FetchResult<null>> {
  return fetch(`/companies/join-requests/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ role }),
  }) as Promise<FetchResult<null>>;
}

// Backend replies `204 No Content` on success — no body.
export function postRejectCompanyJoinRequest(
  id: string,
): Promise<FetchResult<null>> {
  return fetch(`/companies/join-requests/${id}/reject`, {
    method: 'POST',
  }) as Promise<FetchResult<null>>;
}
